import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import { usePageTransition } from '../../utils/pageTransitions';

const AdminLogin = () => {
  const { signIn, user, userProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { variants, transition } = usePageTransition();

  // Redirect if already authenticated with admin role
  useEffect(() => {
    if (!authLoading && user && userProfile) {
      if (userProfile?.role === 'admin' || userProfile?.role === 'manager' || userProfile?.role === 'staff') {
        navigate('/protected-admin-dashboard');
      } else {
        setErrors({ general: 'Access denied. Admin privileges required.' });
      }
    }
  }, [user, userProfile, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      await signIn(credentials?.email, credentials?.password);
      // Auth state change will trigger redirect via useEffect
    } catch (error) {
      setErrors({
        general: error?.message || 'Invalid credentials. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 flex items-center justify-center p-4"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={variants}
      transition={transition}
    >
      <Helmet>
        <title>Admin Portal - ORAZEK Investment</title>
      </Helmet>
      <div className="bg-card rounded-2xl shadow-premium-xl w-full max-w-md border border-border">
        <div className="p-6 md:p-8">
          {/* Header */}
          <div className="flex items-center justify-center mb-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Icon name="Shield" size={32} color="var(--color-primary)" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Admin Portal</h1>
              <p className="text-sm text-muted-foreground">ORAZEK Investment - Secure Access</p>
            </div>
          </div>

          {/* Error Alert */}
          {errors?.general && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
              <div className="flex items-start gap-2">
                <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
                <p className="text-sm text-error whitespace-pre-line">{errors?.general}</p>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              type="email"
              label="Email Address"
              placeholder="admin@orazek.com"
              value={credentials?.email}
              onChange={(e) => setCredentials({ ...credentials, email: e?.target?.value })}
              required
              error={errors?.email}
            />

            <div>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  label="Password"
                  placeholder="Enter your password"
                  value={credentials?.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e?.target?.value })}
                  required
                  error={errors?.password}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                </button>
              </div>
              <div className="flex justify-end mt-1">
                <Link to="/forgot-password" className="text-xs text-primary hover:underline">
                  Forgot Password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              variant="default"
              fullWidth
              iconName="LogIn"
              iconPosition="right"
              className="mt-6"
              disabled={loading || authLoading}
            >
              {loading ? 'Authenticating...' : 'Sign In to Admin Portal'}
            </Button>
          </form>



          {/* Security Notice */}
          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Icon name="Lock" size={14} />
            <span>Secured by Tux Authentication</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AdminLogin;